import React, {useCallback, useEffect, useState} from 'react';
import {Checkbox, FormControlLabel} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import {
    getNextShiftTypeIdx,
    isShiftTypeOptionLocked,
    normalizeShiftTypeIdx,
    ShiftTypeIdx,
    ShiftTypeOption,
} from '../../ShiftTypeFilter.utils';
import {
    ShiftTypeFilterContainer,
    ShiftTypeFilterRow,
    ShiftTypeHint,
} from './ShiftTypeFilter.styles';

interface ShiftTypeFilterProps {
    value?: number;
    onChange: (value: ShiftTypeIdx) => void;
}

const LOCKED_HINT = 'At least one shift type stays selected.';

const ShiftTypeFilter = ({value, onChange}: ShiftTypeFilterProps): JSX.Element => {
    const normalizedValue = normalizeShiftTypeIdx(value);
    const [showLockedHint, setShowLockedHint] = useState(false);

    useEffect(() => {
        if (!showLockedHint) return;
        const timeout = window.setTimeout(() => setShowLockedHint(false), 2500);
        return () => window.clearTimeout(timeout);
    }, [showLockedHint]);

    const handleToggle = useCallback((option: ShiftTypeOption) => {
        if (isShiftTypeOptionLocked(normalizedValue, option)) {
            setShowLockedHint(true);
            return;
        }

        setShowLockedHint(false);
        onChange(getNextShiftTypeIdx(normalizedValue, option));
    }, [normalizedValue, onChange]);

    const dentalChecked = normalizedValue !== 1;
    const adminChecked = normalizedValue !== 2;
    const dentalLocked = isShiftTypeOptionLocked(normalizedValue, 'dental');
    const adminLocked = isShiftTypeOptionLocked(normalizedValue, 'admin');

    const label = (text: string, locked: boolean) => (
        <span className="lockedLabel">
            {text}
            {locked && <LockOutlinedIcon className="lockIcon" aria-hidden="true" data-testid={`${text}-lock`} />}
        </span>
    );

    return (
        <ShiftTypeFilterContainer>
            <ShiftTypeFilterRow role="group" aria-label="Shift Type">
                <span className="shiftTypePrefix">Show:</span>
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={dentalChecked}
                            onChange={() => handleToggle('dental')}
                            inputProps={{
                                'aria-label': 'Dental Assisting',
                                'aria-disabled': dentalLocked,
                            }}
                            sx={{padding: '5px', color: '#0B4598'}}
                        />
                    )}
                    label={label('Dental Assisting', dentalLocked)}
                />
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={adminChecked}
                            onChange={() => handleToggle('admin')}
                            inputProps={{
                                'aria-label': 'Admin/Reception',
                                'aria-disabled': adminLocked,
                            }}
                            sx={{padding: '5px', color: '#0B4598'}}
                        />
                    )}
                    label={label('Admin/Reception', adminLocked)}
                />
            </ShiftTypeFilterRow>
            {showLockedHint && <ShiftTypeHint role="status">{LOCKED_HINT}</ShiftTypeHint>}
        </ShiftTypeFilterContainer>
    );
};

export default ShiftTypeFilter;
